import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertOctagon,
  CheckCircle2,
  Clock3,
  Eye,
  FileSearch,
  Loader2,
  Play,
  RefreshCw,
  RotateCcw,
  ShieldAlert,
  Trash2,
  XCircle,
} from 'lucide-react';

import PermissionGate from '../components/PermissionGate';
import {
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  MetricCard,
  PageHeader,
  SearchInput,
  SegmentedTabs,
  StatusPill,
  cx,
  formatDateTime,
} from '../components/EnterpriseAdminUI';
import { revocationAPI } from '../services/api';
import { useAuth } from '../utils/AuthContext';
import { normalizeRunStatus } from '../utils/revocationEvidence.mjs';

const STATUS_FILTERS = [
  { id: 'all', label: 'All runs' },
  { id: 'active', label: 'In progress' },
  { id: 'success', label: 'Successful' },
  { id: 'failed', label: 'Failed' },
];

function RunStatus({ status }) {
  const normalized = normalizeRunStatus(status);
  const config = {
    success: { label: 'Successful', tone: 'success', icon: CheckCircle2 },
    failed: { label: 'Failed', tone: 'danger', icon: XCircle },
    running: { label: 'Running', tone: 'info', icon: RefreshCw },
    pending: { label: 'Pending', tone: 'warning', icon: Clock3 },
  }[normalized] || { label: status || 'Unknown', tone: 'neutral', icon: Clock3 };

  return <StatusPill {...config} />;
}

function isActiveRun(run) {
  return ['running', 'pending'].includes(normalizeRunStatus(run?.status));
}

function runSearchText(run) {
  return [run.id, run.status, run.trigger_source, run.triggered_by_email, run.started_at, run.finished_at]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export default function RevocationRunsPage() {
  const { hasPermission } = useAuth();
  const canTrigger = hasPermission('trigger_revocation_run');

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [showTriggerConfirm, setShowTriggerConfirm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadRuns = useCallback(async ({ background = false } = {}) => {
    if (background) setRefreshing(true);
    else setLoading(true);

    setError('');

    try {
      const response = await revocationAPI.listRuns();
      setRuns(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to load revocation runs.',
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const hasActiveRun = useMemo(() => runs.some(isActiveRun), [runs]);

  useEffect(() => {
    if (!hasActiveRun) return undefined;

    const timer = window.setInterval(() => {
      loadRuns({ background: true });
    }, 5000);

    return () => window.clearInterval(timer);
  }, [hasActiveRun, loadRuns]);

  const counts = useMemo(() => {
    const summary = { all: runs.length, active: 0, success: 0, failed: 0 };
    runs.forEach((run) => {
      const state = normalizeRunStatus(run.status);
      if (['running', 'pending'].includes(state)) summary.active += 1;
      if (state === 'success') summary.success += 1;
      if (state === 'failed') summary.failed += 1;
    });
    return summary;
  }, [runs]);

  const totals = useMemo(
    () =>
      runs.reduce(
        (summary, run) => ({
          fetched: summary.fetched + Number(run.fetched || 0),
          deleted: summary.deleted + Number(run.deleted || 0),
          retained: summary.retained + Number(run.retained || 0),
          failed: summary.failed + Number(run.failed || 0),
          manualReview: summary.manualReview + Number(run.manual_review || 0),
        }),
        { fetched: 0, deleted: 0, retained: 0, failed: 0, manualReview: 0 },
      ),
    [runs],
  );

  const visibleRuns = useMemo(() => {
    const query = search.trim().toLowerCase();

    return runs.filter((run) => {
      const normalized = normalizeRunStatus(run.status);
      const filterMatch =
        statusFilter === 'all' ||
        (statusFilter === 'active' && ['running', 'pending'].includes(normalized)) ||
        normalized === statusFilter;
      const searchMatch = !query || runSearchText(run).includes(query);
      return filterMatch && searchMatch;
    });
  }, [runs, search, statusFilter]);

  const triggerRun = async () => {
    setTriggering(true);
    setError('');
    setSuccess('');

    try {
      const response = await revocationAPI.triggerRun();
      const runId = response?.data?.run_id;
      const optimisticRun = runId
        ? {
            id: runId,
            status: 'RUNNING',
            started_at: new Date().toISOString(),
            finished_at: null,
            pages_processed: 0,
            fetched: 0,
            deleted: 0,
            retained: 0,
            failed: 0,
            manual_review: 0,
          }
        : null;

      if (optimisticRun) {
        setRuns((current) => [optimisticRun, ...current.filter((run) => run.id !== runId)]);
      }

      setSuccess('Revocation run queued. This page will refresh while it is running.');
      setShowTriggerConfirm(false);
      await loadRuns({ background: true });
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Failed to start a revocation run.',
      );
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
          <span className="text-sm font-bold text-slate-700">Loading revocation operations…</span>
        </div>
      </div>
    );
  }

  return (
    <PermissionGate permission="view_retention_dashboard" showFallback>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Retention operations"
          title="Revocation Runs"
          description="Review item-level deletion, restricted retention, legal holds, retries, and evidence without exposing raw PII."
          icon={RotateCcw}
          actions={
            <>
              <button
                type="button"
                onClick={() => loadRuns({ background: true })}
                disabled={refreshing}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
              >
                <RefreshCw className={cx('h-4 w-4', refreshing && 'animate-spin')} />
                Refresh
              </button>
              {canTrigger && (
                <button
                  type="button"
                  onClick={() => setShowTriggerConfirm(true)}
                  disabled={triggering || hasActiveRun}
                  className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {triggering || hasActiveRun ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {hasActiveRun ? 'Run in progress' : 'Start revocation run'}
                </button>
              )}
            </>
          }
        />

        {error && (
          <InlineAlert tone="danger" title="Revocation operation failed">
            {error}
          </InlineAlert>
        )}
        {success && (
          <InlineAlert tone="success" title="Run accepted">
            {success}
          </InlineAlert>
        )}
        {hasActiveRun && (
          <InlineAlert tone="info" title="A revocation run is active">
            A second run cannot be started until the current operation finishes. Status refreshes automatically every five seconds.
          </InlineAlert>
        )}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard label="Total runs" value={counts.all} hint={`${counts.active} currently active`} icon={FileSearch} />
          <MetricCard label="Revocation requests" value={totals.fetched} hint="Consent records leased" icon={Eye} tone="info" />
          <MetricCard label="Fully deleted records" value={totals.deleted} hint="No retained personal data" icon={Trash2} tone="success" />
          <MetricCard label="Records with retention" value={totals.retained} hint="Contains governed retained data" icon={ShieldAlert} tone={totals.retained ? 'warning' : 'neutral'} />
          <MetricCard label="Needs attention" value={totals.failed + totals.manualReview} hint="Retry or manual review" icon={AlertOctagon} tone={totals.failed + totals.manualReview ? 'danger' : 'neutral'} />
        </div>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-5 sm:p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-950">Run history</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Select a run to inspect deleted, retained, retry-pending, and manual-review evidence.
                </p>
              </div>
              <div className="grid w-full gap-3 sm:grid-cols-[minmax(220px,1fr)_auto] xl:max-w-2xl">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by run ID or status"
                  ariaLabel="Search revocation runs"
                />
                <SegmentedTabs
                  tabs={STATUS_FILTERS.map((item) => ({ ...item, badge: counts[item.id] }))}
                  activeTab={statusFilter}
                  onChange={setStatusFilter}
                  ariaLabel="Filter revocation runs"
                />
              </div>
            </div>
          </div>

          {visibleRuns.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={Clock3}
                title={runs.length ? 'No runs match these filters' : 'No revocation runs yet'}
                description={
                  runs.length
                    ? 'Adjust the search or status filter to find a previous operation.'
                    : canTrigger
                      ? 'Start a controlled revocation run when records must be evaluated against consent and retention state.'
                      : 'No revocation operation has been recorded for this organization.'
                }
              />
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {visibleRuns.map((run) => (
                <article key={run.id} className="p-5 transition hover:bg-slate-50/70 sm:p-6">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <RunStatus status={run.status} />
                        <StatusPill label={run.trigger_source === 'MANUAL' ? 'Manual' : 'Scheduled'} tone="info" />
                        {run.no_work && <StatusPill label="No pending work" tone="neutral" />}
                        <span className="font-mono text-xs text-slate-400">{run.id}</span>
                      </div>
                      <div className="mt-3 grid gap-2 text-sm text-slate-600 sm:grid-cols-2 xl:grid-cols-3">
                        <p><span className="font-bold text-slate-800">Started:</span> {formatDateTime(run.started_at)}</p>
                        <p><span className="font-bold text-slate-800">Finished:</span> {formatDateTime(run.finished_at)}</p>
                        <p><span className="font-bold text-slate-800">Source:</span> {run.trigger_source === 'MANUAL' ? (run.triggered_by_email || 'Administrator') : 'Scheduled service'}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                      <StatusPill label={`${run.fetched || 0} requests`} tone="info" />
                      <StatusPill label={`${run.deleted || 0} fully deleted`} tone="success" />
                      <StatusPill label={`${run.retained || 0} with retention`} tone={run.retained ? 'warning' : 'neutral'} />
                      <StatusPill label={`${run.failed || 0} failed`} tone={run.failed ? 'danger' : 'neutral'} />
                      <StatusPill label={`${run.manual_review || 0} review`} tone={run.manual_review ? 'warning' : 'neutral'} />
                      <Link
                        to={`/revocations/runs/${run.id}`}
                        className="ml-0 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-bold text-slate-700 shadow-sm hover:border-orange-200 hover:text-orange-700 xl:ml-2"
                      >
                        <Eye className="h-4 w-4" />
                        View details
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <ConfirmDialog
          open={showTriggerConfirm}
          title="Start a revocation run?"
          description="This queues an asynchronous retention and consent-revocation operation for the current organization."
          confirmLabel="Start controlled run"
          tone="brand"
          busy={triggering}
          onClose={() => setShowTriggerConfirm(false)}
          onConfirm={triggerRun}
        >
          <div className="space-y-3 text-sm leading-6 text-slate-600">
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <AlertOctagon className="mt-0.5 h-5 w-5 shrink-0" />
              <p>
                This operation can delete eligible PII items or move lawfully retained items into processing-restricted state.
                Failed deletions remain retryable and are never reported as successful.
              </p>
            </div>
          </div>
        </ConfirmDialog>
      </div>
    </PermissionGate>
  );
}
